import { TEN_GENERATION_CONTENT } from "./contentPack.ts";
import { FAMILY_NAME_CANDIDATES, randomNameParts, uniqueFullName } from "./names.ts";
import { createRng, nextRng, pickWeighted, randomInt } from "./rng.ts";
import type {
  AptitudeDomain,
  ContentPack,
  EventChoice,
  EventDefinition,
  HouseholdState,
  LogicGameState,
  PendingChoice,
  PersonState,
  PersonalStats,
  RelationshipCandidate,
  ScheduledConsequence,
} from "./types.ts";

export const LOGIC_SAVE_KEY = "ten-generation-life-unified-save-v1";
export const LEGACY_LOGIC_SAVE_KEY = "ten-generation-life-logic-v2-save-v2";
export const LOGIC_SCHEMA_VERSION = 7;

const BASE_STATS: PersonalStats = { stamina: 52, intellect: 52, charm: 50, empathy: 54, luck: 50, health: 82 };
const clamp = (value: number, min = 0, max = 100) => Math.max(min, Math.min(max, Math.round(value)));
const unique = <T,>(values: T[]) => [...new Set(values)];
const alive = (person: PersonState) => !person.deathYear;
export const ageOf = (person: PersonState, year: number) => Math.max(0, (person.deathYear ?? year) - person.birthYear);
const APTITUDE_DOMAINS: AptitudeDomain[] = ["science", "language", "business", "creative", "sports", "care", "practical", "nature"];

function idFrom(state: LogicGameState, prefix: string) {
  const roll = randomInt(state.rng, 100000, 999999);
  state.rng = roll.rng;
  return `${prefix}-${state.world.year}-${roll.value}`;
}

function personTemplate(id: string, name: string, gender: "female" | "male", birthYear: number, householdId: string): PersonState {
  return {
    id,
    name,
    gender,
    birthYear,
    parentIds: [],
    partnerIds: [],
    relationshipStage: "single",
    childIds: [],
    householdId,
    stats: { ...BASE_STATS },
    education: [],
    career: { industry: "尚未決定", occupation: "成長中", status: "student", level: 0, experience: 0, annualIncome: 0 },
    personality: [],
    traits: [],
    flags: [],
    talents: {},
    discoveredTalents: [],
    interests: [],
    longTermStates: {},
  };
}

function seedTalents(state: LogicGameState, person: PersonState) {
  const countRoll = nextRng(state.rng);
  state.rng = countRoll.rng;
  const standoutCount = countRoll.value < 0.35 ? 0 : countRoll.value < 0.85 ? 1 : 2;
  for (const domain of APTITUDE_DOMAINS) {
    const roll = randomInt(state.rng, 32, 62);
    state.rng = roll.rng;
    person.talents[domain] = roll.value;
  }
  const remaining = [...APTITUDE_DOMAINS];
  for (let index = 0; index < standoutCount; index += 1) {
    const domainRoll = randomInt(state.rng, 0, remaining.length - 1);
    state.rng = domainRoll.rng;
    const [domain] = remaining.splice(domainRoll.value, 1);
    const scoreRoll = randomInt(state.rng, 68, 92);
    state.rng = scoreRoll.rng;
    person.talents[domain] = scoreRoll.value;
  }
  person.longTermStates.innateTalentCount = standoutCount;
}

export function createLogicGame(seed: number | string = Date.now(), familyName = "黑田", protagonistName = "黑田遙"): LogicGameState {
  const year = 2026;
  const householdId = "household-founder";
  const protagonist = personTemplate("person-protagonist-1", protagonistName, "female", year, householdId);
  const father = personTemplate("person-parent-1", `${familyName}誠一`, "male", year - 30, householdId);
  const mother = personTemplate("person-parent-2", `${familyName}美咲`, "female", year - 29, householdId);
  father.career = { industry: "服務業", occupation: "門市主管", status: "employed", level: 2, experience: 8, annualIncome: 150 };
  mother.career = { industry: "行政", occupation: "行政專員", status: "employed", level: 2, experience: 7, annualIncome: 145 };
  father.childIds = [protagonist.id];
  mother.childIds = [protagonist.id];
  father.partnerIds = [mother.id];
  mother.partnerIds = [father.id];
  father.currentPartnerId = mother.id;
  mother.currentPartnerId = father.id;
  father.relationshipStage = "married";
  mother.relationshipStage = "married";
  protagonist.parentIds = [father.id, mother.id];

  const household: HouseholdState = {
    id: householdId,
    name: `${familyName}家`,
    assets: 180,
    memberIds: [father.id, mother.id, protagonist.id],
    dependentIds: [protagonist.id],
    supportShares: {},
    investmentLevel: 0,
    businessLevel: 0,
    familyPlan: "undecided",
    annualLedger: { year, income: 0, expenses: 0, net: 0, details: [] },
    flags: [],
  };

  const initialState: LogicGameState = {
    schemaVersion: LOGIC_SCHEMA_VERSION,
    saveNamespace: "ten-generation-life-unified",
    rng: createRng(seed),
    people: { [protagonist.id]: protagonist, [father.id]: father, [mother.id]: mother },
    households: { [household.id]: household },
    dynasty: { familyName, founderId: protagonist.id, generationByPersonId: { [protagonist.id]: 1, [father.id]: 0, [mother.id]: 0 }, familyBusinessLevel: 0, familyFlags: [] },
    world: { year, era: "現代", mode: "standard", generationArc: 1, modifiers: { ...TEN_GENERATION_CONTENT.generationArcs[0].modifiers }, flags: [] },
    activePersonId: protagonist.id,
    activeHouseholdId: household.id,
    eventLedger: { occurrences: {}, topicLastYear: {}, permanentFlags: [] },
    pendingChoice: null,
    annualFlow: null,
    logs: [{ year, personId: protagonist.id, title: `第 1 代：${protagonist.name} 出生`, text: "一段新的家族人生開始。", tags: ["birth"] }],
    completedGenerations: 0,
    newbornIdsAwaitingName: [],
    scheduledConsequences: [],
  };
  father.name = uniqueFullName(initialState, "male", { familyName });
  mother.name = uniqueFullName(initialState, "female", { familyName });
  seedTalents(initialState, protagonist);
  seedTalents(initialState, father);
  seedTalents(initialState, mother);
  return initialState;
}

export function activePerson(state: LogicGameState) {
  return state.people[state.activePersonId];
}

export function activeHousehold(state: LogicGameState) {
  return state.households[state.activeHouseholdId];
}

function applyStats(stats: PersonalStats, effects: Partial<PersonalStats> = {}) {
  const next = { ...stats };
  (Object.keys(next) as Array<keyof PersonalStats>).forEach((key) => {
    next[key] = clamp(next[key] + Number(effects[key] || 0));
  });
  return next;
}

function hasPartner(state: LogicGameState, person: PersonState) {
  const partner = person.currentPartnerId ? state.people[person.currentPartnerId] : undefined;
  return person.relationshipStage !== "single" && Boolean(partner && alive(partner));
}

function currentPartner(state: LogicGameState, person = activePerson(state)) {
  const partner = person.currentPartnerId ? state.people[person.currentPartnerId] : undefined;
  return partner && alive(partner) ? partner : undefined;
}

function sharedChildrenWithCurrentPartner(state: LogicGameState, person = activePerson(state)) {
  const partner = currentPartner(state, person);
  if (!partner) return [];
  return person.childIds
    .map((id) => state.people[id])
    .filter((child): child is PersonState => Boolean(child && alive(child) && child.parentIds.includes(partner.id)));
}

function datingYears(state: LogicGameState, person = activePerson(state)) {
  const since = Number(person.longTermStates.datingSinceYear ?? state.world.year);
  return Math.max(0, state.world.year - since + 1);
}

function setIncomeRate(person: PersonState, startYear: number, endYear: number, rate: number) {
  person.longTermStates.incomeRateStartYear = startYear;
  person.longTermStates.incomeRateEndYear = endYear;
  person.longTermStates.incomeRate = Math.max(0, Math.min(1, rate));
}

function incomeRateFor(person: PersonState, year: number) {
  const start = Number(person.longTermStates.incomeRateStartYear || 0);
  const end = Number(person.longTermStates.incomeRateEndYear || 0);
  if (!start || year < start || year > end) return 1;
  return Number(person.longTermStates.incomeRate ?? 1);
}

const STAT_NAMES: Record<keyof PersonalStats, string> = {
  stamina: "體力",
  intellect: "智力",
  charm: "魅力",
  empathy: "共感",
  luck: "運氣",
  health: "健康",
};

function compactEffectText(effects: EventChoice["effects"]) {
  const parts: string[] = [];
  if (effects.assets) parts.push(`家庭資金 ${effects.assets > 0 ? "+" : ""}${effects.assets}`);
  for (const [key, raw] of Object.entries(effects.stats || {})) {
    const value = Number(raw || 0);
    if (value) parts.push(`${STAT_NAMES[key as keyof PersonalStats]} ${value > 0 ? "+" : ""}${value}`);
  }
  if (effects.careerIncomeDelta) parts.push(`年收入 ${effects.careerIncomeDelta > 0 ? "+" : ""}${effects.careerIncomeDelta}`);
  if (effects.careerLevelDelta) parts.push(`職級 ${effects.careerLevelDelta > 0 ? "+" : ""}${effects.careerLevelDelta}`);
  return parts.join("、");
}

function conditionsPass(state: LogicGameState, event: EventDefinition, person: PersonState) {
  const conditions = event.conditions || {};
  const age = ageOf(person, state.world.year);
  const household = state.households[person.householdId];
  const educationUntil = Number(person.longTermStates.educationUntil || 0);
  return (
    (conditions.alive === undefined || conditions.alive === alive(person)) &&
    (conditions.minAge === undefined || age >= conditions.minAge) &&
    (conditions.maxAge === undefined || age <= conditions.maxAge) &&
    (conditions.exactAge === undefined || age === conditions.exactAge) &&
    (conditions.partnered === undefined || conditions.partnered === hasPartner(state, person)) &&
    (conditions.hasChildren === undefined || conditions.hasChildren === person.childIds.some((id) => state.people[id] && alive(state.people[id]))) &&
    (conditions.gender === undefined || conditions.gender === person.gender) &&
    (conditions.careerStatus === undefined || conditions.careerStatus === person.career.status) &&
    (conditions.careerStatuses === undefined || conditions.careerStatuses.includes(person.career.status)) &&
    (conditions.minCareerLevel === undefined || person.career.level >= conditions.minCareerLevel) &&
    (conditions.maxCareerLevel === undefined || person.career.level <= conditions.maxCareerLevel) &&
    (conditions.minCareerExperience === undefined || person.career.experience >= conditions.minCareerExperience) &&
    (conditions.relationshipStage === undefined || conditions.relationshipStage === person.relationshipStage) &&
    (conditions.familyPlan === undefined || household.familyPlan === conditions.familyPlan) &&
    (conditions.householdAssetsMin === undefined || household.assets >= conditions.householdAssetsMin) &&
    (conditions.requiredTraits || []).every((trait) => person.traits.includes(trait)) &&
    (conditions.requiredFlags || []).every((flag) => person.flags.includes(flag)) &&
    !(conditions.excludedFlags || []).some((flag) => person.flags.includes(flag)) &&
    (conditions.requiredWorldFlags || []).every((flag) => state.world.flags.includes(flag))
    && !(event.topic === "career" && educationUntil > state.world.year)
  );
}

export function canPersonConceiveWithPartner(state: LogicGameState, person: PersonState) {
  const partner = person.currentPartnerId ? state.people[person.currentPartnerId] : undefined;
  if (!partner || !alive(partner) || person.relationshipStage !== "married" || partner.gender === person.gender) return false;
  const candidate = person.gender === "female" ? person : partner;
  const age = ageOf(candidate, state.world.year);
  return age >= 18 && age <= 50;
}

export function choiceAvailable(state: LogicGameState, person: PersonState, choice: EventChoice) {
  const household = state.households[person.householdId];
  if (!household) return false;
  if (choice.requires?.householdAssetsMin !== undefined && household.assets < choice.requires.householdAssetsMin) return false;
  if (choice.requires?.canConceive && !canPersonConceiveWithPartner(state, person)) return false;
  if (choice.requires?.anyOf?.length && !choice.requires.anyOf.some((requirement) => {
    if (requirement.kind === "stat") return person.stats[requirement.stat] >= requirement.min;
    if (requirement.kind === "interest") return person.interests.includes(requirement.domain);
    if (requirement.kind === "talent") return Number(person.talents[requirement.domain] || 0) >= requirement.min;
    return person.traits.includes(requirement.trait);
  })) return false;
  return true;
}

function descendantIds(state: LogicGameState, person: PersonState) {
  const found = new Set<string>();
  const queue = [...person.childIds];
  while (queue.length) {
    const id = queue.shift()!;
    if (found.has(id) || !state.people[id]) continue;
    found.add(id);
    queue.push(...state.people[id].childIds);
  }
  return [...found];
}

function siblingIds(state: LogicGameState, person: PersonState) {
  const found = new Set<string>();
  person.parentIds.forEach((parentId) => {
    state.people[parentId]?.childIds.forEach((childId) => {
      if (childId !== person.id) found.add(childId);
    });
  });
  return [...found];
}

function eventTargetRelations(event: EventDefinition) {
  return event.targetRelations?.length
    ? event.targetRelations
    : event.targetRelation
      ? [event.targetRelation]
      : [];
}

export function eligibleEventTargets(state: LogicGameState, event: EventDefinition, actor = activePerson(state)) {
  const relations = eventTargetRelations(event);
  if (!relations.length) return [];
  const relatedIds = unique(relations.flatMap((relation) => relation === "parent"
    ? actor.parentIds
    : relation === "sibling"
      ? siblingIds(state, actor)
      : relation === "partner"
        ? actor.partnerIds
        : descendantIds(state, actor)));
  const conditions = event.targetConditions || {};
  return relatedIds.map((id) => state.people[id]).filter((target): target is PersonState => {
    if (!target || !alive(target) || target.householdId !== actor.householdId) return false;
    const bondingUntil = Number(target.longTermStates.adoptionEducationDueYear || 0);
    if (bondingUntil && state.world.year < bondingUntil) return false;
    if (event.sharedChildWithCurrentPartner) {
      const partner = currentPartner(state, actor);
      if (!partner || !target.parentIds.includes(actor.id) || !target.parentIds.includes(partner.id)) return false;
    }
    const age = ageOf(target, state.world.year);
    return (conditions.minAge === undefined || age >= conditions.minAge)
      && (conditions.maxAge === undefined || age <= conditions.maxAge)
      && (conditions.alive === undefined || conditions.alive === alive(target))
      && (conditions.relationshipStage === undefined || conditions.relationshipStage === target.relationshipStage)
      && (conditions.careerStatus === undefined || conditions.careerStatus === target.career.status)
      && (conditions.careerStatuses === undefined || conditions.careerStatuses.includes(target.career.status));
  });
}

function ledgerOwner(scope: EventDefinition["scope"], actor: PersonState) {
  if (scope === "household") return `household-${actor.householdId}`;
  if (scope === "dynasty") return "dynasty";
  if (scope === "world") return "world";
  return actor.id;
}

function eventLedgerOwner(event: EventDefinition, actor: PersonState) {
  return ledgerOwner(event.scope, actor);
}

function themeOccurrenceKey(event: EventDefinition, actor: PersonState) {
  if (!event.themeKey) return undefined;
  return `${ledgerOwner(event.themeScope || event.scope, actor)}:theme:${event.themeKey}`;
}

function eventOccurrenceKey(event: EventDefinition, actor: PersonState) {
  return `${eventLedgerOwner(event, actor)}:${event.id}`;
}

export function eventEligible(state: LogicGameState, event: EventDefinition, person = activePerson(state), target?: PersonState) {
  const bondingUntil = Number(person.longTermStates.adoptionEducationDueYear || 0);
  if (bondingUntil && state.world.year < bondingUntil) return false;
  if (!alive(person) || !conditionsPass(state, event, person)) return false;
  if (eventTargetRelations(event).length && (!target || !eligibleEventTargets(state, event, person).some((candidate) => candidate.id === target.id))) return false;
  const owner = eventLedgerOwner(event, person);
  const occurrence = state.eventLedger.occurrences[`${owner}:${event.id}`];
  const count = occurrence?.count || 0;
  if ((event.once || event.repeatable === false) && count > 0) return false;
  if (event.maxOccurrences !== undefined && count >= event.maxOccurrences) return false;
  if (occurrence && state.world.year - occurrence.lastYear < (event.cooldownYears || 0)) return false;
  const topicYear = state.eventLedger.topicLastYear[`${owner}:${event.topic}`];
  if (topicYear !== undefined && state.world.year - topicYear < (event.topicCooldownYears || 0)) return false;
  const themeKey = themeOccurrenceKey(event, person);
  const themeOccurrence = themeKey ? state.eventLedger.occurrences[themeKey] : undefined;
  if (themeOccurrence && event.themeMaxOccurrences !== undefined && themeOccurrence.count >= event.themeMaxOccurrences) return false;
  if (themeOccurrence && state.world.year - themeOccurrence.lastYear < (event.themeCooldownYears || 0)) return false;
  return true;
}

function eventWeight(state: LogicGameState, event: EventDefinition, content: ContentPack) {
  const arc = content.generationArcs.find((item) => item.id === state.world.generationArc);
  const person = activePerson(state);
  const discoveredStrength = person.discoveredTalents.length
    ? person.discoveredTalents.reduce((total, domain) => total + Number(person.talents[domain] || 50), 0) / person.discoveredTalents.length
    : 50;
  const careerAffinity = Number(person.longTermStates.careerAffinity || 50);
  const aptitudeMultiplier = event.topic === "career"
    ? 0.8 + discoveredStrength / 600 + careerAffinity / 500 + Math.min(0.15, person.interests.length * 0.02)
    : 1;
  const reunionMultiplier = event.id === "marriage-decision" && person.longTermStates.reunitedRelationship ? 2 : 1;
  return event.weight * reunionMultiplier * (arc?.eventTopics.includes(event.topic) ? arc.weightMultiplier : 1) * aptitudeMultiplier;
}

function careerDomain(industry: string, occupation: string): AptitudeDomain {
  const value = `${industry}${occupation}`;
  if (/科技|軟體|資料|研究/.test(value)) return "science";
  if (/金融|商業|營運|管理/.test(value)) return "business";
  if (/醫療|照護|健康/.test(value)) return "care";
  if (/教育|教學|語言/.test(value)) return "language";
  if (/文化|創意|設計|內容/.test(value)) return "creative";
  if (/技術|工藝|製造/.test(value)) return "practical";
  return "business";
}

const ADULT_CAREERS: Record<AptitudeDomain, Pick<PersonState["career"], "industry" | "occupation" | "annualIncome">> = {
  science: { industry: "科技服務", occupation: "資料與技術專員", annualIncome: 118 },
  language: { industry: "教育出版", occupation: "內容與語言專員", annualIncome: 108 },
  business: { industry: "商業服務", occupation: "營運專員", annualIncome: 116 },
  creative: { industry: "文化創意", occupation: "設計與內容專員", annualIncome: 104 },
  sports: { industry: "運動休閒", occupation: "運動服務專員", annualIncome: 102 },
  care: { industry: "醫療照護", occupation: "照護服務專員", annualIncome: 110 },
  practical: { industry: "技術製造", occupation: "技術人員", annualIncome: 112 },
  nature: { industry: "環境產業", occupation: "環境與農務專員", annualIncome: 104 },
};

function strongestCareerDomain(person: PersonState): AptitudeDomain {
  const preferred = person.interests
    .map((domain) => ({ domain, score: Number(person.talents[domain] || 50) + 12 }))
    .sort((left, right) => right.score - left.score || left.domain.localeCompare(right.domain))[0];
  if (preferred) return preferred.domain;
  return APTITUDE_DOMAINS
    .map((domain) => ({ domain, score: Number(person.talents[domain] || 50) }))
    .sort((left, right) => right.score - left.score || left.domain.localeCompare(right.domain))[0]?.domain || "business";
}

export function ensureAdultCareer(
  state: LogicGameState,
  person: PersonState,
  options: { recordLog?: boolean } = {},
) {
  const age = ageOf(person, state.world.year);
  const educationUntil = Number(person.longTermStates.educationUntil || 0);
  if (!alive(person) || person.career.status !== "student" || age < 22 || educationUntil > state.world.year) return false;
  const domain = strongestCareerDomain(person);
  const template = ADULT_CAREERS[domain];
  const experience = Math.max(0, age - 22);
  const level = Math.min(5, 1 + Math.floor(experience / 9));
  person.career = {
    ...template,
    status: "employed",
    level,
    experience,
    annualIncome: template.annualIncome + Math.max(0, level - 1) * 16,
  };
  person.flags = unique([...person.flags, "career-started"]);
  person.longTermStates.careerAffinity = Math.min(100, Number(person.talents[domain] || 50) + (person.interests.includes(domain) ? 12 : 0));
  if (options.recordLog !== false) {
    state.logs.push({
      year: state.world.year,
      personId: person.id,
      title: `${person.name} 開始職涯`,
      text: `${person.name} 完成目前學習階段，進入${template.industry}擔任${template.occupation}；收入自今年起計入所屬家庭戶。`,
      tags: ["career", "household"],
    });
  }
  return true;
}

export function selectNextEvent(state: LogicGameState, content: ContentPack = TEN_GENERATION_CONTENT) {
  const actor = activePerson(state);
  const candidates = content.events.flatMap((event) => {
    if (event.requiredStage) return [];
    if (!eventTargetRelations(event).length) return eventEligible(state, event, actor) ? [{ event, targetPersonIds: [] as string[] }] : [];
    const targetPersonIds = eligibleEventTargets(state, event, actor)
      .filter((target) => eventEligible(state, event, actor, target))
      .map((target) => target.id);
    return targetPersonIds.length ? [{ event, targetPersonIds }] : [];
  });
  const exact = candidates.filter(({ event }) => event.conditions?.exactAge === ageOf(actor, state.world.year));
  let pool = exact.length ? exact : candidates;
  if (!exact.length) {
    const encounter = candidates.find(({ event }) => event.relationshipEncounter);
    if (encounter) {
      const age = ageOf(actor, state.world.year);
      const chance = encounterProbability(age);
      const roll = nextRng(state.rng);
      state.rng = roll.rng;
      if (roll.value < chance) return { event: encounter.event, targetPersonId: undefined };
      pool = pool.filter(({ event }) => !event.relationshipEncounter);
    }
    const selectable = pool.filter(({ event }) => !event.passive && event.choices.length >= 2);
    const passive = pool.filter(({ event }) => event.passive || event.choices.length === 1);
    if (selectable.length && passive.length) {
      const selectableLastYears = content.events
        .filter((event) => !event.requiredStage && !event.passive && event.choices.length >= 2)
        .map((event) => state.eventLedger.occurrences[eventOccurrenceKey(event, actor)]?.lastYear)
        .filter((year): year is number => year !== undefined);
      const lastSelectableYear = selectableLastYears.length ? Math.max(...selectableLastYears) : undefined;
      const age = ageOf(actor, state.world.year);
      const maxGap = age <= 24 ? 2 : 3;
      const selectableOverdue = lastSelectableYear === undefined || state.world.year - lastSelectableYear > maxGap;
      if (selectableOverdue) {
        pool = selectable;
      } else {
        const modeRoll = nextRng(state.rng);
        state.rng = modeRoll.rng;
        pool = modeRoll.value < 0.55 ? selectable : passive;
      }
    } else if (selectable.length) {
      pool = selectable;
    }
  }
  const picked = pickWeighted(state.rng, pool, ({ event }) => eventWeight(state, event, content));
  state.rng = picked.rng;
  if (!picked.value) return undefined;
  if (!picked.value.targetPersonIds.length) return { event: picked.value.event, targetPersonId: undefined };
  const targetRoll = randomInt(state.rng, 0, picked.value.targetPersonIds.length - 1);
  state.rng = targetRoll.rng;
  return { event: picked.value.event, targetPersonId: picked.value.targetPersonIds[targetRoll.value] };
}

export function encounterProbability(age: number) {
  if (age < 14 || age > 58) return 0;
  const baseChance = age < 18 ? 0.12 : age < 30 ? 0.18 : age < 45 ? 0.16 : 0.14;
  return Number((baseChance * 3).toFixed(2));
}

function recordEvent(state: LogicGameState, event: EventDefinition, actor: PersonState) {
  const owner = eventLedgerOwner(event, actor);
  const key = `${owner}:${event.id}`;
  const previous = state.eventLedger.occurrences[key];
  state.eventLedger.occurrences[key] = { count: (previous?.count || 0) + 1, lastYear: state.world.year };
  state.eventLedger.topicLastYear[`${owner}:${event.topic}`] = state.world.year;
  const themeKey = themeOccurrenceKey(event, actor);
  if (themeKey) {
    const previousTheme = state.eventLedger.occurrences[themeKey];
    state.eventLedger.occurrences[themeKey] = {
      count: (previousTheme?.count || 0) + 1,
      lastYear: state.world.year,
    };
  }
}

const ENCOUNTER_SCENES = [
  {
    context: "社團活動",
    minAge: 14, maxAge: 25,
    introduction: "{name} 在收拾器材時發現你還沒走，順手把一疊資料分了一半。你們一路聊到校門口，才知道彼此常注意同一件小事。",
  },
  {
    context: "朋友聚會",
    minAge: 14, maxAge: 75,
    introduction: "朋友臨時多帶了一個人來。{name} 沒有急著加入最熱鬧的話題，卻在你落單時問起剛才沒說完的故事。",
  },
  {
    context: "打工換班",
    minAge: 16, maxAge: 35,
    introduction: "忙亂的交班時段，{name} 替你接住一位難纏的客人。事情結束後，你們坐在門口喝完一杯快融掉的飲料。",
  },
  {
    context: "圖書館",
    minAge: 14, maxAge: 75,
    introduction: "你和 {name} 同時伸手拿向架上最後一本書。誰也沒有立刻放手，最後乾脆約好輪流讀，再交換各自留下的筆記。",
  },
  {
    context: "興趣課程",
    minAge: 16, maxAge: 85,
    introduction: "{name} 坐在你旁邊，作品和你的方向完全不同。下課後，對方主動問你為什麼做出那個選擇，談話意外延續很久。",
  },
  {
    context: "工作合作",
    minAge: 18, maxAge: 65,
    introduction: "一場臨時合作讓你認識 {name}。對方記得你隨口提過的困難，隔天帶來一份整理好的資料，也留下一句不帶壓力的邀請。",
  },
  {
    context: "社區活動",
    minAge: 18, maxAge: 85,
    introduction: "雨突然落下時，{name} 把傘往你這邊挪了一點。短短一段同行，你們從附近新開的店聊到各自最近的生活。",
  },
  {
    context: "線上社群",
    minAge: 14, maxAge: 75,
    introduction: "你和 {name} 因同一篇文章交換看法，幾次留言後才發現住得不遠。第一次見面沒有戲劇性的巧合，卻比預期自在。",
  },
];

function relationshipCandidate(state: LogicGameState, actor: PersonState, gender: "female" | "male"): RelationshipCandidate {
  const stat = (min: number, max: number) => {
    const roll = randomInt(state.rng, min, max);
    state.rng = roll.rng;
    return roll.value;
  };
  const actorAge = ageOf(actor, state.world.year);
  const ageOffset = stat(-3, 4);
  const age = Math.max(16, actorAge + ageOffset);
  const stats: PersonalStats = {
    stamina: stat(38, 78),
    intellect: stat(38, 82),
    charm: stat(38, 82),
    empathy: stat(38, 82),
    luck: stat(28, 82),
    health: stat(52, 92),
  };
  const career = age < 18
    ? { industry: "就學中", occupation: "學生", status: "student" as const, level: 0, experience: 0, annualIncome: 0 }
    : stats.intellect >= 68
      ? { industry: "專業服務", occupation: "研究與企劃人員", status: "employed" as const, level: 1, experience: Math.max(0, age - 22), annualIncome: 122 }
      : stats.charm >= 66
        ? { industry: "商業服務", occupation: "客戶與營運專員", status: "employed" as const, level: 1, experience: Math.max(0, age - 20), annualIncome: 114 }
        : { industry: "技術服務", occupation: "技術人員", status: "employed" as const, level: 1, experience: Math.max(0, age - 19), annualIncome: 108 };
  const eligibleScenes = ENCOUNTER_SCENES.filter((item) => actorAge >= item.minAge && actorAge <= item.maxAge);
  const sceneRoll = randomInt(state.rng, 0, eligibleScenes.length - 1);
  state.rng = sceneRoll.rng;
  const scene = eligibleScenes[sceneRoll.value];
  const candidate: RelationshipCandidate = {
    id: idFrom(state, "candidate"),
    name: uniqueFullName(state, gender),
    gender,
    birthYear: state.world.year - age,
    stats,
    career,
    encounterContext: scene.context,
  };
  candidate.introduction = scene.introduction.replaceAll("{name}", candidate.name);
  return candidate;
}

function createPendingChoice(state: LogicGameState, event: EventDefinition, targetPersonId?: string): PendingChoice {
  const person = activePerson(state);
  const subject = targetPersonId && !eventTargetRelations(event).length ? state.people[targetPersonId] : person;
  const candidates: Record<string, RelationshipCandidate> = {};
  if (event.relationshipEncounter) {
    const formerPartners = subject.partnerIds
      .map((id) => state.people[id])
      .filter((person): person is PersonState => Boolean(person && alive(person) && person.relationshipStage === "single"));
    const reunionRoll = nextRng(state.rng);
    state.rng = reunionRoll.rng;
    const former = formerPartners.length && reunionRoll.value < 0.2 ? formerPartners[0] : undefined;
    const candidate = former
      ? {
          id: former.id,
          name: former.name,
          gender: former.gender,
          birthYear: former.birthYear,
          stats: { ...former.stats },
          career: { ...former.career },
          encounterContext: "久別重逢",
          introduction: `你在一個沒有預期的場合再次遇見 ${former.name}。彼此都沒有假裝過去不存在，只是先問了一句：「最近過得好嗎？」`,
        }
      : (() => {
          const orientationRoll = nextRng(state.rng);
          state.rng = orientationRoll.rng;
          const opposite: "female" | "male" = subject.gender === "female" ? "male" : "female";
          return relationshipCandidate(state, subject, orientationRoll.value < 0.2 ? subject.gender : opposite);
        })();
    for (const choice of event.choices) {
      if (choice.effects.action === "start-dating-candidate") candidates[choice.id] = candidate;
    }
  } else {
    for (const choice of event.choices) {
      if (choice.effects.action === "start-dating-male") candidates[choice.id] = relationshipCandidate(state, subject, "male");
      if (choice.effects.action === "start-dating-female") candidates[choice.id] = relationshipCandidate(state, subject, "female");
    }
  }
  const encounter = Object.values(candidates)[0];
  let narrative = event.text;
  if (event.narrativeVariants?.length) {
    const narrativeRoll = randomInt(state.rng, 0, event.narrativeVariants.length - 1);
    state.rng = narrativeRoll.rng;
    narrative = event.narrativeVariants[narrativeRoll.value];
  }
  narrative = narrative
    .replaceAll("{relative}", targetPersonId ? state.people[targetPersonId]?.name || "家人" : "家人")
    .replaceAll("{partner}", subject.currentPartnerId ? state.people[subject.currentPartnerId]?.name || "交往對象" : "交往對象");
  return {
    id: idFrom(state, "choice"),
    eventId: event.id,
    actorId: person.id,
    targetPersonId,
    householdId: person.householdId,
    createdYear: state.world.year,
    validChoiceIds: event.choices.filter((choice) => choiceAvailable(state, subject, choice)).map((choice) => choice.id),
    ...(Object.keys(candidates).length ? { candidates } : {}),
    ...(encounter?.introduction ? {
      displayTitle: `${encounter.encounterContext}裡認識的 ${encounter.name}`,
      displayText: encounter.introduction,
    } : event.narrativeVariants?.length ? { displayText: narrative } : {}),
  };
}

function annualHouseholdLedger(state: LogicGameState, household: HouseholdState) {
  const livingMembers = household.memberIds.map((id) => state.people[id]).filter((person): person is PersonState => Boolean(person && alive(person)));
  let income = 0;
  let expenses = 0;
  const details: string[] = [];
  for (const person of livingMembers) {
    const personAge = ageOf(person, state.world.year);
    if (personAge >= 18 && person.career.status !== "unemployed" && person.career.status !== "student") {
      const earnedIncome = Math.round(person.career.annualIncome * incomeRateFor(person, state.world.year));
      income += earnedIncome;
      details.push(`${person.name}收入 +${earnedIncome}`);
    }
    // Values use the same abstract annual unit as salaries. These costs keep a
    // two-income household stable without making wealth automatic before the
    // player makes education, career, investment, or business decisions.
    const externallySupportedStudent = household.id.startsWith("household-dating")
      && person.career.status === "student"
      && personAge < 22;
    const cost = externallySupportedStudent ? 0 : personAge < 18 ? 52 : personAge >= 65 ? 80 : 105;
    expenses += cost;
    if (cost) details.push(`${person.name}生活支出 -${cost}`);
  }
  const investmentIncome = household.investmentLevel * 28;
  const businessIncome = household.businessLevel * 42;
  income += investmentIncome + businessIncome;
  if (investmentIncome) details.push(`家庭投資收入 +${investmentIncome}`);
  if (businessIncome) details.push(`家業收入 +${businessIncome}`);
  expenses += household.businessLevel * 18;
  if (household.businessLevel) details.push(`家業營運支出 -${household.businessLevel * 18}`);
  for (const cost of household.recurringCareCosts || []) {
    if (state.world.year < cost.startYear || state.world.year > cost.endYear) continue;
    expenses += cost.annualAmount;
    details.push(`${cost.label} -${cost.annualAmount}`);
  }
  const net = income - expenses;
  household.assets = Math.max(-1800, Math.round(household.assets + net));
  if (household.assets <= -1800) household.flags = unique([...household.flags, "insolvency"]);
  household.annualLedger = { year: state.world.year, income, expenses, net, details };
}

function processPregnancies(state: LogicGameState) {
  for (const household of Object.values(state.households)) {
    const pregnancy = household.pregnancy;
    if (!pregnancy || pregnancy.dueYear > state.world.year) continue;
    const gestational = state.people[pregnancy.gestationalParentId];
    const responsible = state.people[pregnancy.responsibleParentId || pregnancy.gestationalParentId];
    const partner = pregnancy.partnerId ? state.people[pregnancy.partnerId] : currentPartner(state, responsible);
    if (!gestational || !responsible || !alive(gestational) || !alive(responsible)) {
      household.pregnancy = undefined;
      continue;
    }
    const successChance = pregnancy.successChance ?? (pregnancy.protectedLeave === false ? 0.88 : 1);
    if (successChance < 1) {
      const outcome = nextRng(state.rng);
      state.rng = outcome.rng;
      if (outcome.value >= successChance) {
        state.logs.push({
          year: state.world.year,
          personId: gestational.id,
          title: "這次懷孕沒有繼續",
          text: "家庭經歷失落並重新安排生活。這不是任何人的錯。",
          tags: ["pregnancy", "loss"],
        });
        household.pregnancy = undefined;
        continue;
      }
    }
    for (let index = 0; index < pregnancy.fetuses; index += 1) {
      const child = createChild(state, gestational, responsible, partner, household, index);
      state.people[child.id] = child;
      household.memberIds.push(child.id);
      household.dependentIds.push(child.id);
      child.parentIds.forEach((parentId) => {
        state.people[parentId].childIds = unique([...state.people[parentId].childIds, child.id]);
      });
      state.dynasty.generationByPersonId[child.id] = (state.dynasty.generationByPersonId[responsible.id] || state.world.generationArc) + 1;
      state.newbornIdsAwaitingName.push(child.id);
      state.logs.push({ year: state.world.year, personId: child.id, title: `${child.name} 出生`, text: "新成員加入同住家庭。", tags: ["birth"] });
    }
    household.lastBirthYear = state.world.year;
    household.pregnancy = undefined;
  }
}

function createChild(
  state: LogicGameState,
  gestational: PersonState,
  responsible: PersonState,
  partner: PersonState | undefined,
  household: HouseholdState,
  siblingIndex: number,
) {
  const genderRoll = nextRng(state.rng);
  state.rng = genderRoll.rng;
  const gender = genderRoll.value < 0.5 ? "female" : "male";
  const generatedName = uniqueFullName(state, gender, { familyName: state.dynasty.familyName, preferDynastyName: true });
  const child = personTemplate(idFrom(state, `person-${siblingIndex}`), generatedName, gender, state.world.year, household.id);
  child.parentIds = unique([gestational.id, responsible.id, ...(partner ? [partner.id] : [])]);
  const otherParent = partner && partner.id !== gestational.id ? partner : responsible.id !== gestational.id ? responsible : undefined;
  const average = (key: keyof PersonalStats) => otherParent
    ? (gestational.stats[key] + otherParent.stats[key]) / 2
    : gestational.stats[key];
  const stats = { ...child.stats };
  (Object.keys(stats) as Array<keyof PersonalStats>).forEach((key) => {
    const variance = randomInt(state.rng, -14, 14);
    state.rng = variance.rng;
    stats[key] = clamp(26 + average(key) * 0.48 + variance.value);
  });
  child.stats = stats;
  seedTalents(state, child);
  return child;
}

export function gestationalParent(state: LogicGameState, household = activeHousehold(state)) {
  const protagonist = activePerson(state);
  if (protagonist.householdId !== household.id) return undefined;
  const partner = currentPartner(state, protagonist);
  if (!partner || protagonist.relationshipStage === "single" || partner.gender === protagonist.gender) return undefined;
  const candidate = protagonist.gender === "female" ? protagonist : partner;
  const age = ageOf(candidate, state.world.year);
  return age >= 18 && age <= 55 ? candidate : undefined;
}

export function conceptionProbability(age: number) {
  if (age < 18 || age > 55) return 0;
  if (age <= 22) return 0.2;
  if (age <= 34) return 0.35;
  if (age <= 44) return 0.25;
  return 0.1;
}

export function multipleChildProbabilities(age: number) {
  if (age < 18 || age > 60) return { two: 0, three: 0 };
  if (age <= 22) return { two: 0.03, three: 0.01 };
  if (age <= 34) return { two: 0.04, three: 0.02 };
  if (age <= 44) return { two: 0.05, three: 0.03 };
  return { two: 0.05, three: 0.02 };
}

export function rollChildCount(state: LogicGameState, one: number, two: number, three: number): 0 | 1 | 2 | 3 {
  const oneRoll = nextRng(state.rng);
  state.rng = oneRoll.rng;
  const twoRoll = nextRng(state.rng);
  state.rng = twoRoll.rng;
  const threeRoll = nextRng(state.rng);
  state.rng = threeRoll.rng;
  if (threeRoll.value < three) return 3;
  if (twoRoll.value < two) return 2;
  if (oneRoll.value < one) return 1;
  return 0;
}

export function maybeStartPregnancy(state: LogicGameState, household = activeHousehold(state)) {
  if (household.pregnancy || household.adoptionPlacement || state.adoptionNameDecision) return false;
  if (household.lastBirthYear !== undefined && state.world.year - household.lastBirthYear < 1) return false;
  const parent = gestationalParent(state, household);
  if (!parent) return false;
  const protagonist = activePerson(state);
  const partner = currentPartner(state, protagonist);
  if (!partner) return false;
  const age = ageOf(parent, state.world.year);
  const one = protagonist.relationshipStage === "married"
    ? conceptionProbability(age)
    : age >= 35 && age <= 55
      ? 0.12
      : 0;
  if (!one) return false;
  const multiples = multipleChildProbabilities(age);
  const fetuses = rollChildCount(state, one, multiples.two, multiples.three);
  if (!fetuses) return false;
  household.pregnancy = {
    gestationalParentId: parent.id,
    responsibleParentId: protagonist.id,
    partnerId: partner.id,
    conceivedYear: state.world.year,
    dueYear: state.world.year + 1,
    fetuses,
    decisionPending: true,
    protectedLeave: false,
  };
  state.logs.push({ year: state.world.year, personId: parent.id, title: "得知懷孕", text: `這次懷孕預計有 ${fetuses} 名孩子，家庭需要決定接下來一年的安排。`, tags: ["pregnancy"] });
  return true;
}

export function adoptionProbabilities(state: LogicGameState) {
  const protagonist = activePerson(state);
  const partner = currentPartner(state, protagonist);
  if (!partner || protagonist.relationshipStage === "single") return undefined;
  if (partner.gender === protagonist.gender) {
    const age = Math.max(ageOf(protagonist, state.world.year), ageOf(partner, state.world.year));
    if (protagonist.relationshipStage === "dating") {
      return age >= 45 && age <= 60 ? { one: 0.2, two: 0.05, three: 0.02 } : undefined;
    }
    const one = conceptionProbability(age);
    if (!one) return undefined;
    return { one, ...multipleChildProbabilities(age) };
  }
  if (protagonist.relationshipStage !== "married") return undefined;
  const youngerAge = Math.min(ageOf(protagonist, state.world.year), ageOf(partner, state.world.year));
  return youngerAge >= 50 && youngerAge <= 65 ? { one: 0.05, two: 0.025, three: 0.01 } : undefined;
}

export function maybeStartAdoption(state: LogicGameState, household = activeHousehold(state)) {
  if (household.pregnancy || household.adoptionPlacement || state.adoptionNameDecision) return false;
  if (household.lastBirthYear !== undefined && state.world.year - household.lastBirthYear < 1) return false;
  if (household.lastAdoptionYear !== undefined && state.world.year - household.lastAdoptionYear < 2) return false;
  const probabilities = adoptionProbabilities(state);
  if (!probabilities) return false;
  const count = rollChildCount(state, probabilities.one, probabilities.two, probabilities.three);
  if (!count) return false;
  household.adoptionPlacement = { count, decisionPending: true };
  return true;
}

const EDUCATION_STAGE_BY_AGE = [
  { min: 3, max: 5, eventId: "kindergarten-choice", stage: "kindergarten", years: 3 },
  { min: 6, max: 11, eventId: "elementary-choice", stage: "elementary", years: 6 },
  { min: 12, max: 14, eventId: "junior-choice", stage: "junior", years: 3 },
  { min: 15, max: 17, eventId: "senior-choice", stage: "senior", years: 3 },
] as const;

function createAdoptedChildren(state: LogicGameState, count: 1 | 2 | 3) {
  const parent = activePerson(state);
  const partner = currentPartner(state, parent);
  const household = activeHousehold(state);
  if (!partner) return [];

  const kindRoll = nextRng(state.rng);
  state.rng = kindRoll.rng;
  const placementKind = count === 1
    ? "single" as const
    : kindRoll.value < 0.35
      ? "multiple" as const
      : kindRoll.value < 0.85
        ? "siblings" as const
        : "shared-placement" as const;
  const ages: number[] = [];
  if (placementKind === "multiple") {
    const ageRoll = randomInt(state.rng, 0, 16);
    state.rng = ageRoll.rng;
    ages.push(...Array.from({ length: count }, () => ageRoll.value));
  } else {
    for (let index = 0; index < count; index += 1) {
      const ageRoll = randomInt(state.rng, 0, 16);
      state.rng = ageRoll.rng;
      ages.push(ageRoll.value);
    }
    if (count > 1 && ages.every((age) => age === ages[0])) {
      ages[ages.length - 1] = (ages[0] + 1) % 17;
    }
  }

  let sharedFamilyName: string | undefined;
  const placementFamilyNames: string[] = [];
  const childIds: string[] = [];
  const originalNames: Record<string, { familyName: string; givenName: string }> = {};
  for (let index = 0; index < count; index += 1) {
    const genderRoll = nextRng(state.rng);
    state.rng = genderRoll.rng;
    const gender = genderRoll.value < 0.5 ? "female" : "male";
    let selectedFamilyName = sharedFamilyName;
    if (placementKind === "shared-placement") {
      const availableFamilies = FAMILY_NAME_CANDIDATES.filter((familyName) => !placementFamilyNames.includes(familyName));
      const familyRoll = randomInt(state.rng, 0, availableFamilies.length - 1);
      state.rng = familyRoll.rng;
      selectedFamilyName = availableFamilies[familyRoll.value];
    }
    const name = randomNameParts(state, gender, selectedFamilyName ? { familyName: selectedFamilyName } : {});
    if (placementKind === "multiple" || placementKind === "siblings") sharedFamilyName ||= name.familyName;
    placementFamilyNames.push(name.familyName);
    const child = personTemplate(idFrom(state, `person-adopted-${index + 1}`), name.fullName, gender, state.world.year - ages[index], household.id);
    seedTalents(state, child);
    child.flags.push("adopted");
    child.parentIds = [parent.id, partner.id];
    child.longTermStates.adoptedYear = state.world.year;
    child.longTermStates.adoptionEducationDueYear = state.world.year + 1;
    child.longTermStates.adoptionOriginalFamilyName = name.familyName;
    child.longTermStates.adoptionOriginalGivenName = name.givenName;
    const statRoll = (min: number, max: number) => {
      const roll = randomInt(state.rng, min, max);
      state.rng = roll.rng;
      return roll.value;
    };
    child.stats = {
      stamina: statRoll(34, 74), intellect: statRoll(34, 78), charm: statRoll(34, 76),
      empathy: statRoll(38, 82), luck: statRoll(25, 85), health: statRoll(48, 90),
    };
    state.people[child.id] = child;
    household.memberIds.push(child.id);
    household.dependentIds.push(child.id);
    parent.childIds = unique([...parent.childIds, child.id]);
    partner.childIds = unique([...partner.childIds, child.id]);
    state.dynasty.generationByPersonId[child.id] = (state.dynasty.generationByPersonId[parent.id] || state.world.generationArc) + 1;
    childIds.push(child.id);
    originalNames[child.id] = { familyName: name.familyName, givenName: name.givenName };
  }
  state.adoptionNameDecision = { childIds, originalNames, placementKind };
  return childIds;
}

function adoptionCaregivers(state: LogicGameState) {
  const parent = activePerson(state);
  const partner = currentPartner(state, parent);
  return partner ? [parent, partner] : [parent];
}

function finishAdoptionCare(state: LogicGameState) {
  const household = activeHousehold(state);
  household.lastAdoptionYear = state.world.year;
  household.adoptionPlacement = undefined;
}

function applyCareStats(person: PersonState, effects: Partial<PersonalStats>) {
  person.stats = applyStats(person.stats, effects);
}

function adoptionCareEndYear(state: LogicGameState, childIds: string[]) {
  const youngChildren = childIds
    .map((id) => state.people[id])
    .filter((child): child is PersonState => Boolean(child && ageOf(child, state.world.year) <= 2));
  return {
    youngChildren,
    endYear: youngChildren.length
      ? Math.max(state.world.year + 1, ...youngChildren.map((child) => child.birthYear + 2))
      : state.world.year + 1,
  };
}

function marriageBonusSelection(state: LogicGameState, content: ContentPack) {
  if (!state.annualFlow || state.annualFlow.marriageBonusChecked) return undefined;
  state.annualFlow.marriageBonusChecked = true;
  const person = activePerson(state);
  if (person.relationshipStage !== "dating") return undefined;
  const event = content.events.find((candidate) => candidate.id === "marriage-decision");
  if (!event) return undefined;
  const hasSharedChild = sharedChildrenWithCurrentPartner(state, person).length > 0;
  if (hasSharedChild) return { event, targetPersonId: undefined, bypassEligibility: true };
  if (!eventEligible(state, event, person)) return undefined;
  const years = datingYears(state, person);
  if (years < 3) return undefined;
  const reunited = Boolean(person.longTermStates.reunitedRelationship);
  const addedChance = marriageDurationBonusProbability(years, reunited);
  const roll = nextRng(state.rng);
  state.rng = roll.rng;
  return roll.value < addedChance ? { event, targetPersonId: undefined, bypassEligibility: true } : undefined;
}

export function marriageDurationBonusProbability(years: number, reunited = false) {
  if (years < 3) return 0;
  const chance = reunited
    ? Math.min(0.8, (years - 2) * 0.1)
    : Math.min(0.6, (years - 2) * 0.05);
  return Number(chance.toFixed(2));
}

export function processMortality(state: LogicGameState) {
  const deaths: PersonState[] = [];
  for (const person of Object.values(state.people)) {
    if (!alive(person)) continue;
    const age = ageOf(person, state.world.year);
    if (age < 45 && person.stats.health > 0) continue;
    const baseRisk = age < 55 ? 0.002 : age < 70 ? 0.012 : age < 85 ? 0.055 : 0.18;
    const healthRisk = Math.max(0, 50 - person.stats.health) / 180;
    const roll = nextRng(state.rng);
    state.rng = roll.rng;
    if (person.stats.health <= 0 || roll.value < baseRisk + healthRisk) {
      person.deathYear = state.world.year;
      person.deathCause = person.stats.health <= 0 ? "健康耗盡" : age >= 85 ? "高齡辭世" : "疾病或意外";
      deaths.push(person);
      person.partnerIds.forEach((partnerId) => {
        const partner = state.people[partnerId];
        if (partner?.currentPartnerId === person.id && alive(partner)) {
          partner.currentPartnerId = undefined;
          partner.relationshipStage = "single";
          partner.traits = unique([...partner.traits, "經歷喪偶"]);
        }
      });
      const household = state.households[person.householdId];
      if (household) {
        household.memberIds = household.memberIds.filter((id) => id !== person.id);
        household.dependentIds = household.dependentIds.filter((id) => id !== person.id);
        delete household.supportShares[person.id];
      }
    }
  }
  deaths.forEach((person, index) => state.logs.push({
    year: state.world.year,
    personId: person.id,
    title: index === 0 ? `${person.name} 的人生落幕` : `${person.name} 於同年離世`,
    text: person.deathCause || "人生告一段落。",
    tags: ["death"],
  }));
  return deaths;
}

function updateCareerAndHouseholds(state: LogicGameState) {
  for (const person of Object.values(state.people)) {
    if (!alive(person)) continue;
    const age = ageOf(person, state.world.year);
    if (person.householdId !== state.activeHouseholdId) ensureAdultCareer(state, person);
    const resumeYear = Number(person.longTermStates.careerResumeYear || 0);
    if (person.career.status === "unemployed" && resumeYear && state.world.year >= resumeYear) {
      person.career.status = "employed";
      person.career.annualIncome = Number(person.longTermStates.previousCareerIncome || 95);
      person.career.occupation = String(person.longTermStates.previousOccupation || person.career.occupation);
      delete person.longTermStates.careerResumeYear;
      delete person.longTermStates.previousCareerIncome;
      delete person.longTermStates.previousOccupation;
      state.logs.push({ year: state.world.year, personId: person.id, title: `${person.name} 回到工作崗位`, text: "收入自今年起恢復計算。", tags: ["career"] });
    }
    if (age >= 65 && person.career.status !== "retired") {
      person.career.status = "retired";
      person.career.annualIncome = Math.round(person.career.annualIncome * 0.35);
    } else if (["employed", "self-employed"].includes(person.career.status)) {
      person.career.experience += 1;
    }
    const household = state.households[person.householdId];
    if (household && age >= 18) household.dependentIds = household.dependentIds.filter((id) => id !== person.id);
  }
  Object.values(state.households).forEach((household) => annualHouseholdLedger(state, household));
}

function requiredStageSelection(state: LogicGameState, content: ContentPack) {
  const actor = activePerson(state);
  const household = activeHousehold(state);
  if (household.pregnancy?.decisionPending) {
    const pregnancyEvent = content.events.find((event) => event.id === "pregnancy-decision");
    if (pregnancyEvent) return { event: pregnancyEvent, targetPersonId: undefined };
  }
  if (household.adoptionPlacement?.decisionPending) {
    const event = content.events.find((candidate) => candidate.id === "adoption-decision");
    if (event) {
      const count = household.adoptionPlacement.count;
      return {
        event,
        targetPersonId: undefined,
        displayTitle: count === 1 ? "一名孩子需要新的家庭" : `${count} 名孩子需要一起被領養`,
        displayText: `這次需要完整承接 ${count} 名孩子。接受前只會知道人數；年齡、性別與姓名會在決定後說明。`,
      };
    }
  }
  if (household.adoptionPlacement?.careDecisionPending) {
    const event = content.events.find((candidate) => candidate.id === "adoption-care-decision");
    if (event) return { event, targetPersonId: undefined };
  }
  if (household.adoptionPlacement?.primaryCaregiverPending) {
    const event = content.events.find((candidate) => candidate.id === "adoption-primary-caregiver");
    if (event) return { event, targetPersonId: undefined };
  }
  const members = household.memberIds
    .map((id) => state.people[id])
    .filter((person): person is PersonState => Boolean(person && alive(person)))
    .sort((left, right) => Number(right.id === actor.id) - Number(left.id === actor.id) || left.birthYear - right.birthYear || left.id.localeCompare(right.id));
  for (const member of members) {
    const dueYear = Number(member.longTermStates.adoptionEducationDueYear || 0);
    if (!dueYear || state.world.year < dueYear) continue;
    const age = ageOf(member, state.world.year);
    const stage = EDUCATION_STAGE_BY_AGE.find((candidate) => age >= candidate.min && age <= candidate.max);
    if (!stage || member.education.some((record) => record.stage === stage.stage)) {
      delete member.longTermStates.adoptionEducationDueYear;
      continue;
    }
    const event = content.events.find((candidate) => candidate.id === stage.eventId);
    if (event) {
      const remainingYears = stage.max - age + 1;
      return {
        event,
        targetPersonId: member.id === actor.id ? undefined : member.id,
        bypassEligibility: true,
        adoptionEducation: true,
        educationEffectScale: remainingYears / stage.years,
        displayTitle: `替 ${member.name} 選擇目前的${event.title}`,
        displayText: `${member.name} 已完成一年的家庭適應。現在從 ${age} 歲所在的學習階段開始安排；費用與能力變化按剩餘 ${remainingYears} 學年計算，不追溯先前年級。`,
      };
    }
  }
  for (const member of members) {
    const event = content.events.find((candidate) => (
      candidate.requiredStage
      && !["pregnancy-decision", "adoption-decision", "adoption-care-decision", "adoption-primary-caregiver"].includes(candidate.id)
      && eventEligible(state, candidate, member)
    ));
    if (event) return { event, targetPersonId: member.id === actor.id ? undefined : member.id };
  }
  return undefined;
}

export function queueNextChoice(state: LogicGameState, content: ContentPack = TEN_GENERATION_CONTENT) {
  if (state.pendingChoice || !state.annualFlow || state.annualFlow.year !== state.world.year) return state.pendingChoice;
  if (state.adoptionNameDecision) return null;
  const required = requiredStageSelection(state, content);
  if (required) {
    state.pendingChoice = createPendingChoice(state, required.event, required.targetPersonId);
    state.pendingChoice.bypassEligibility = required.bypassEligibility;
    state.pendingChoice.adoptionEducation = required.adoptionEducation;
    state.pendingChoice.educationEffectScale = required.educationEffectScale;
    state.pendingChoice.displayTitle = required.displayTitle || state.pendingChoice.displayTitle;
    state.pendingChoice.displayText = required.displayText || state.pendingChoice.displayText;
    return state.pendingChoice;
  }
  const marriage = marriageBonusSelection(state, content);
  if (marriage) {
    state.pendingChoice = createPendingChoice(state, marriage.event, marriage.targetPersonId);
    state.pendingChoice.bypassEligibility = marriage.bypassEligibility;
    return state.pendingChoice;
  }
  if (state.annualFlow.randomEventComplete) return null;
  state.annualFlow.randomEventComplete = true;
  const selected = selectNextEvent(state, content);
  if (selected?.event.passive && selected.event.choices.length === 1) {
    const actor = activePerson(state);
    const target = selected.targetPersonId ? state.people[selected.targetPersonId] : undefined;
    const subject = target && !eventTargetRelations(selected.event).length ? target : actor;
    const choice = selected.event.choices[0];
    if (choiceAvailable(state, subject, choice)) {
      applyChoiceEffects(state, subject, choice.effects, `${selected.event.title}：${subject.name}`, choice.id);
      if (target && eventTargetRelations(selected.event).length && choice.targetEffects) {
        applyChoiceEffects(state, target, choice.targetEffects, `${selected.event.title}：${target.name}`, choice.id);
      }
      recordEvent(state, selected.event, subject);
      state.logs.push({
        year: state.world.year,
        personId: (target || actor).id,
        title: selected.event.title,
        text: (() => {
          let narrative = selected.event.text;
          if (selected.event.narrativeVariants?.length) {
            const narrativeRoll = randomInt(state.rng, 0, selected.event.narrativeVariants.length - 1);
            state.rng = narrativeRoll.rng;
            narrative = selected.event.narrativeVariants[narrativeRoll.value];
          }
          narrative = narrative.replaceAll("{relative}", target?.name || "家人");
          const results = [
          compactEffectText(choice.effects),
          target && choice.targetEffects ? `${target.name}：${compactEffectText(choice.targetEffects)}` : "",
          ].filter(Boolean);
          return results.length ? `${narrative} 結果：${results.join("；")}` : narrative;
        })(),
        tags: [selected.event.topic, "event"],
      });
    }
  } else if (selected) {
    state.pendingChoice = createPendingChoice(state, selected.event, selected.targetPersonId);
  }
  return state.pendingChoice;
}

function processScheduledConsequences(state: LogicGameState) {
  const remaining: ScheduledConsequence[] = [];
  for (const consequence of state.scheduledConsequences) {
    if (consequence.dueYear > state.world.year) {
      remaining.push(consequence);
      continue;
    }
    const actor = state.people[consequence.actorId];
    const target = consequence.targetPersonId ? state.people[consequence.targetPersonId] : undefined;
    if (!actor || !alive(actor) || actor.householdId !== consequence.householdId
      || (target && (!alive(target) || target.householdId !== consequence.householdId))) {
      continue;
    }
    const roll = nextRng(state.rng);
    state.rng = roll.rng;
    const success = roll.value >= consequence.risk;
    const household = state.households[consequence.householdId];
    if (!household) continue;
    const recordMoney = (amount: number, detail: string) => {
      household.assets += amount;
      if (household.annualLedger.year === state.world.year) {
        if (amount >= 0) household.annualLedger.income += amount;
        else household.annualLedger.expenses += Math.abs(amount);
        household.annualLedger.net += amount;
        household.annualLedger.details.push(`${detail} ${amount >= 0 ? "+" : ""}${amount}`);
      }
    };
    let title = "去年的決定有了結果";
    let text = "";
    if (consequence.kind === "family-business") {
      if (success) {
        const incomeGain = consequence.stage > 1 ? 34 : 18;
        if (target) {
          target.career = {
            ...target.career,
            industry: "自營事業",
            occupation: consequence.stage > 1 ? "小店經營者" : "籌備中的經營者",
            status: "self-employed",
            level: Math.max(1, target.career.level),
            annualIncome: Math.max(target.career.annualIncome, incomeGain + 72),
          };
          target.traits = unique([...target.traits, "經營小生意"]);
        }
        if (consequence.stage > 1) household.businessLevel += 1;
        text = `${target?.name || "家人"} 把去年的計畫一步步做起來，${consequence.stage > 1 ? "客源終於穩定，收入開始固定進入家庭帳本。" : "第一輪測試得到足以繼續的回應。"}`;
      } else {
        const loss = consequence.stage > 1 ? 65 : 28;
        recordMoney(-loss, `${target?.name || "家人"}生意後續`);
        if (target) {
          target.stats = applyStats(target.stats, { health: -2, intellect: 2 });
          if (consequence.stage > 1) {
            target.career.status = "unemployed";
            target.career.annualIncome = 0;
            target.longTermStates.careerResumeYear = state.world.year + 2;
            target.longTermStates.previousCareerIncome = 92;
            target.longTermStates.previousOccupation = "一般專員";
          }
        }
        text = `${target?.name || "家人"} 的生意沒有照預期發展，家庭再支出 ${loss} 處理存貨與合約，並重新安排工作。`;
      }
      title = `${target?.name || "家人"} 的小生意有了後續`;
    } else if (consequence.kind === "family-loan") {
      const repayment = consequence.sourceChoiceId.startsWith("lend-full") ? 45 : 19;
      if (success) {
        recordMoney(repayment, `${target?.name || "家人"}歸還借款`);
        if (target) {
          target.stats = applyStats(target.stats, { intellect: 2, health: 1 });
          target.traits = unique([...target.traits, "如期歸還家庭借款"]);
        }
        title = `${target?.name || "家人"} 歸還了借款`;
        text = `${target?.name || "家人"} 按約定整理支出並歸還 ${repayment}，去年的周轉沒有變成長期缺口。`;
      } else {
        if (target) {
          target.stats = applyStats(target.stats, { health: -2, empathy: -1 });
          target.traits = unique([...target.traits, "尚未清償家庭借款"]);
        }
        title = `${target?.name || "家人"} 暫時無法還款`;
        text = `${target?.name || "家人"} 的財務仍未恢復，去年的借款暫時收不回來；家庭沒有再追加資金，但關係多了一層壓力。`;
      }
    } else if (consequence.kind === "guarantee") {
      const delta = success ? 25 : -220;
      recordMoney(delta, "擔保案後續");
      title = "擔保案的結果到來";
      text = success
        ? `借款人按時清償，${target?.name || "家人"} 終於放下心中的石頭，家庭也取回一筆費用。`
        : `借款人無法清償，家庭因去年的擔保承擔 ${Math.abs(delta)}，共同資金受到明顯衝擊。`;
    } else if (consequence.kind === "lottery") {
      const delta = success ? (consequence.risk >= 0.9 ? 900 : 260) : 0;
      if (delta) recordMoney(delta, "彩券獎金");
      title = success ? "那張彩券真的中了獎" : "彩券開獎了";
      text = success
        ? `你核對了好幾次號碼，扣除必要費用後仍有 ${delta} 進入家庭帳本。這是少見的好運，不是固定收入。`
        : "開出的號碼沒有帶來獎金；支出只停在當初設定的娛樂預算，沒有再追買。";
    } else if (consequence.kind === "opportunity") {
      const delta = success ? 95 : -12;
      recordMoney(delta, "特殊機緣後續");
      actor.stats = applyStats(actor.stats, success ? { charm: 3, luck: 4 } : { health: -1, intellect: 2 });
      title = success ? "那次機會打開了新的路" : "那次嘗試沒有入選";
      text = success
        ? `去年的準備得到回應，合作、獎助或公開機會帶來 ${delta}，也讓你接觸到原本進不去的圈子。`
        : `結果沒有如願，家庭支出 ${Math.abs(delta)} 處理交通與材料；完成的作品和經驗仍留了下來。`;
    } else if (consequence.kind === "dispute") {
      const delta = success ? 58 : -26;
      recordMoney(delta, "爭議處理結果");
      actor.stats = applyStats(actor.stats, success ? { intellect: 2, health: 1 } : { health: -2, empathy: -1 });
      title = success ? "申訴得到正式回應" : "爭議只能止損收尾";
      text = success
        ? `紀錄與程序發揮作用，家庭取回或免付 ${delta}，責任也被寫清楚。`
        : `證據不足或協商破裂，家庭再支出 ${Math.abs(delta)} 收尾；你沒有繼續投入無上限的時間與金錢。`;
    } else {
      const delta = success ? 170 : -75;
      recordMoney(delta, "遺產爭議後續");
      title = "遺產爭議告一段落";
      text = success
        ? `去年的處理方式讓帳目得到釐清，${target?.name || "家人"} 分得的款項有一部分進入共同家庭。`
        : `協商沒有完全成功，家庭又支付 ${Math.abs(delta)} 的程序與往返費用，關係也留下裂痕。`;
      if (!success && target) target.traits = unique([...target.traits, "家族關係緊張"]);
    }
    state.logs.push({ year: state.world.year, personId: target?.id || actor.id, title, text, tags: ["follow-up", consequence.kind] });
    if (consequence.kind === "family-business" && consequence.stagesRemaining > 1) {
      remaining.push({
        ...consequence,
        dueYear: state.world.year + 1,
        stage: consequence.stage + 1,
        stagesRemaining: consequence.stagesRemaining - 1,
        risk: Math.max(0.05, Math.min(0.9, consequence.risk + (success ? -0.12 : 0.12))),
      });
    }
  }
  state.scheduledConsequences = remaining;
}

export function advanceLogicYear(source: LogicGameState, content: ContentPack = TEN_GENERATION_CONTENT) {
  if (source.pendingChoice) throw new Error("Resolve the current choice before advancing the year");
  const state = structuredClone(source);
  state.world.year += 1;
  processPregnancies(state);
  processMortality(state);
  updateCareerAndHouseholds(state);
  processScheduledConsequences(state);
  const pregnancyStarted = maybeStartPregnancy(state);
  if (!pregnancyStarted) maybeStartAdoption(state);
  const active = activePerson(state);
  if (!active || !alive(active)) return state;
  state.annualFlow = { year: state.world.year, randomEventComplete: false, marriageBonusChecked: false };
  queueNextChoice(state, content);
  return state;
}

function applyChoiceEffects(
  state: LogicGameState,
  person: PersonState,
  effects: EventChoice["effects"],
  detail: string,
  choiceId: string,
  candidate?: RelationshipCandidate,
) {
  const household = state.households[person.householdId];
  person.stats = applyStats(person.stats, effects.stats);
  person.traits = unique([...person.traits, ...(effects.traits || [])]);
  person.flags = unique([...person.flags, ...(effects.flags || [])]);
  Object.assign(person.longTermStates, effects.longTermStates || {});
  if (effects.educationYears) person.longTermStates.educationUntil = state.world.year + effects.educationYears;
  if (effects.interests?.length) {
    person.interests = unique([...person.interests, ...effects.interests]).slice(-4);
  }
  person.discoveredTalents = unique([...person.discoveredTalents, ...(effects.discoverTalents || [])]);
  if (effects.revealInnateTalents?.length) {
    const revealed = effects.revealInnateTalents.filter((domain) => Number(person.talents[domain] || 0) >= 68);
    person.discoveredTalents = unique([...person.discoveredTalents, ...revealed]);
  }
  for (const [domain, delta] of Object.entries(effects.talentDelta || {})) {
    const key = domain as AptitudeDomain;
    person.talents[key] = clamp(Number(person.talents[key] || 50) + Number(delta || 0));
  }
  const assetEffect = effects.assets || 0;
  household.assets += assetEffect;
  if (assetEffect && household.annualLedger.year === state.world.year) {
    if (assetEffect > 0) household.annualLedger.income += assetEffect;
    else household.annualLedger.expenses += Math.abs(assetEffect);
    household.annualLedger.net += assetEffect;
    household.annualLedger.details.push(`${detail} ${assetEffect > 0 ? "+" : ""}${assetEffect}`);
  }
  household.flags = unique([...household.flags, ...(effects.householdFlags || [])]);
  state.world.flags = unique([...state.world.flags, ...(effects.worldFlags || [])]);
  if (effects.familyPlan) household.familyPlan = effects.familyPlan;
  if (effects.career) {
    person.career = { ...person.career, ...effects.career };
    if (effects.career.status === "employed" || effects.career.status === "self-employed") {
      const domain = careerDomain(person.career.industry, person.career.occupation);
      const talent = Number(person.talents[domain] || 50);
      person.longTermStates.careerAffinity = Math.min(100, talent + (person.interests.includes(domain) ? 12 : 0));
      if (person.discoveredTalents.includes(domain) && talent >= 72) {
        person.traits = unique([...person.traits, "職涯發揮所長"]);
      }
    }
  }
  if (effects.careerIncomeDelta !== undefined) {
    person.career.annualIncome = Math.max(0, person.career.annualIncome + effects.careerIncomeDelta);
  }
  if (effects.careerLevelDelta !== undefined) {
    person.career.level = Math.max(0, person.career.level + effects.careerLevelDelta);
  }
  if (effects.careerPauseYears) {
    person.longTermStates.previousCareerIncome = person.career.annualIncome;
    person.longTermStates.previousOccupation = person.career.occupation;
    // The choice is made after this year's ledger has already been settled.
    // Add one so a one-year pause actually removes the next full year's income.
    person.longTermStates.careerResumeYear = state.world.year + effects.careerPauseYears + 1;
    person.career.status = "unemployed";
    person.career.annualIncome = 0;
  }
  household.investmentLevel = Math.max(0, household.investmentLevel + Number(effects.investmentLevelDelta || 0));
  household.businessLevel = Math.max(0, household.businessLevel + Number(effects.businessLevelDelta || 0));
  const educationFlag = effects.flags?.find((flag) => flag.startsWith("education-"));
  if (educationFlag && !person.education.some((record) => record.stage === educationFlag.replace("education-", ""))) {
    person.education.push({ stage: educationFlag.replace("education-", ""), choiceId, year: state.world.year });
  }
  if ((effects.action === "start-dating-male" || effects.action === "start-dating-female" || effects.action === "start-dating-candidate") && candidate) {
    startDatingCandidate(state, person.id, candidate);
  }
  if (effects.action === "marry-partner") marryCurrentPartner(state, person.id);
  if (effects.action === "marry-generated-partner" && person.relationshipStage === "single" && ageOf(person, state.world.year) >= 18) {
    const orientationRoll = nextRng(state.rng);
    state.rng = orientationRoll.rng;
    const opposite = person.gender === "female" ? "male" : "female";
    const partnerGender = orientationRoll.value < 0.2 ? person.gender : opposite;
    addPartner(state, person.id, uniqueFullName(state, partnerGender), partnerGender);
  }
  if (effects.action === "end-relationship") endRelationship(state, person.id);
  if (effects.action === "adopt-child") adoptChild(state, person.id);
  if (effects.action === "leave-household") moveAdultToNewHousehold(state, person.id);
  if (effects.action === "protect-pregnancy" && household.pregnancy) {
    household.pregnancy.decisionPending = false;
    household.pregnancy.protectedLeave = true;
    household.pregnancy.successChance = 1;
    const pregnant = state.people[household.pregnancy.gestationalParentId];
    if (pregnant) setIncomeRate(pregnant, state.world.year + 1, state.world.year + 1, 0);
  }
  if (effects.action === "reduce-work-pregnancy" && household.pregnancy) {
    household.pregnancy.decisionPending = false;
    household.pregnancy.protectedLeave = false;
    household.pregnancy.successChance = 0.85;
    const pregnant = state.people[household.pregnancy.gestationalParentId];
    if (pregnant) setIncomeRate(pregnant, state.world.year + 1, state.world.year + 1, 0.5);
  }
  if (effects.action === "continue-pregnancy" && household.pregnancy) {
    household.pregnancy.decisionPending = false;
    household.pregnancy.protectedLeave = false;
    household.pregnancy.successChance = 0.5;
    const pregnant = state.people[household.pregnancy.gestationalParentId];
    if (pregnant) setIncomeRate(pregnant, state.world.year + 1, state.world.year + 1, 1);
  }
  if (effects.action === "end-pregnancy" && household.pregnancy) {
    const pregnant = state.people[household.pregnancy.gestationalParentId];
    if (pregnant) pregnant.stats = applyStats(pregnant.stats, { empathy: -2, health: -3 });
    household.pregnancy = undefined;
  }
  if (effects.action === "accept-adoption" && household.adoptionPlacement?.decisionPending) {
    const placement = household.adoptionPlacement;
    placement.decisionPending = false;
    placement.childIds = createAdoptedChildren(state, placement.count);
    placement.careDecisionPending = placement.childIds.length > 0;
  }
  if (effects.action === "decline-adoption") {
    household.adoptionPlacement = undefined;
  }
  if (effects.action === "adoption-care-both" && household.adoptionPlacement) {
    for (const caregiver of adoptionCaregivers(state)) {
      setIncomeRate(caregiver, state.world.year + 1, state.world.year + 1, 0);
      applyCareStats(caregiver, { empathy: 5, health: 2 });
    }
    finishAdoptionCare(state);
  }
  if (effects.action === "adoption-care-primary" && household.adoptionPlacement) {
    household.adoptionPlacement.careDecisionPending = false;
    household.adoptionPlacement.primaryCaregiverPending = true;
  }
  if (effects.action === "adoption-care-balance" && household.adoptionPlacement) {
    const { endYear } = adoptionCareEndYear(state, household.adoptionPlacement.childIds || []);
    for (const caregiver of adoptionCaregivers(state)) {
      setIncomeRate(caregiver, state.world.year + 1, endYear, 0.6);
      applyCareStats(caregiver, { empathy: 5, charm: 3, health: -1 });
    }
    finishAdoptionCare(state);
  }
  if (effects.action === "adoption-care-external" && household.adoptionPlacement) {
    const childIds = household.adoptionPlacement.childIds || [];
    for (const caregiver of adoptionCaregivers(state)) applyCareStats(caregiver, { empathy: -2 });
    const { youngChildren, endYear } = adoptionCareEndYear(state, childIds);
    household.recurringCareCosts ||= [];
    household.recurringCareCosts.push({
      id: idFrom(state, "adoption-care"), annualAmount: 100,
      startYear: state.world.year + 1, endYear, childIds,
      label: youngChildren.length ? "領養後托育支援" : "領養後課業與生活支援",
    });
    finishAdoptionCare(state);
  }
  if ((effects.action === "adoption-primary-actor" || effects.action === "adoption-primary-partner") && household.adoptionPlacement) {
    const caregivers = adoptionCaregivers(state);
    const caregiver = effects.action === "adoption-primary-partner" ? caregivers[1] : caregivers[0];
    if (caregiver) {
      setIncomeRate(caregiver, state.world.year + 1, state.world.year + 1, 0);
      applyCareStats(caregiver, { empathy: 2, charm: -2, health: 1 });
    }
    finishAdoptionCare(state);
  }
}

function scaledEducationEffects(effects: EventChoice["effects"], scale: number) {
  if (scale >= 1) return effects;
  const stats = effects.stats
    ? Object.fromEntries(Object.entries(effects.stats).map(([key, value]) => [key, Math.round(Number(value || 0) * scale)])) as Partial<PersonalStats>
    : undefined;
  return {
    ...effects,
    ...(stats ? { stats } : {}),
    ...(effects.assets !== undefined ? { assets: Math.round(effects.assets * scale) } : {}),
    ...(effects.careerIncomeDelta !== undefined ? { careerIncomeDelta: Math.round(effects.careerIncomeDelta * scale) } : {}),
  };
}

export function resolveLogicChoice(source: LogicGameState, choiceId: string, content: ContentPack = TEN_GENERATION_CONTENT) {
  if (!source.pendingChoice) throw new Error("No pending choice");
  const state = structuredClone(source);
  const pending = state.pendingChoice!;
  const event = content.events.find((item) => item.id === pending.eventId);
  const choice = event?.choices.find((item) => item.id === choiceId);
  const actor = state.people[pending.actorId];
  const target = pending.targetPersonId ? state.people[pending.targetPersonId] : undefined;
  if (!event || !choice || !actor || (pending.targetPersonId && !target) || !pending.validChoiceIds.includes(choiceId)) throw new Error("Invalid pending choice");
  if (!pending.bypassEligibility && target && (target.householdId !== pending.householdId || (eventTargetRelations(event).length
    ? !eventEligible(state, event, actor, target)
    : !eventEligible(state, event, target)))) throw new Error("Pending choice target is no longer eligible");
  const subject = target && !eventTargetRelations(event).length ? target : actor;
  if (!choiceAvailable(state, subject, choice)) throw new Error("Choice requirements are no longer met");
  const candidate = pending.candidates?.[choice.id];
  const effects = pending.adoptionEducation
    ? scaledEducationEffects(choice.effects, pending.educationEffectScale || 1)
    : choice.effects;
  applyChoiceEffects(state, subject, effects, `${event.title}：${subject.name}`, choice.id, candidate);
  if (target && eventTargetRelations(event).length && choice.targetEffects) applyChoiceEffects(state, target, choice.targetEffects, `${event.title}：${target.name}`, choice.id);
  if (choice.followUp) {
    state.scheduledConsequences.push({
      id: idFrom(state, "follow-up"),
      kind: choice.followUp.kind,
      sourceEventId: event.id,
      sourceChoiceId: choice.id,
      actorId: actor.id,
      targetPersonId: target?.id,
      householdId: pending.householdId,
      dueYear: state.world.year + (choice.followUp.delayYears || 1),
      stage: 1,
      stagesRemaining: choice.followUp.stages || 1,
      risk: choice.followUp.risk,
    });
  }
  recordEvent(state, event, target && !eventTargetRelations(event).length ? target : actor);
  if (pending.adoptionEducation) delete subject.longTermStates.adoptionEducationDueYear;
  const targetLabel = target ? `（${target.name}）` : "";
  state.logs.push({ year: state.world.year, personId: (target || actor).id, title: `${event.title}${targetLabel}：${choice.label}`, text: choice.text, tags: [event.topic, "choice"] });
  state.pendingChoice = null;
  queueNextChoice(state, content);
  return state;
}

export function addPartner(state: LogicGameState, personId = state.activePersonId, name?: string, gender: "female" | "male" = "male") {
  const person = state.people[personId];
  const usedNames = new Set(Object.values(state.people).map((member) => member.name));
  const requestedName = name || uniqueFullName(state, gender);
  const uniqueName = usedNames.has(requestedName) ? uniqueFullName(state, gender) : requestedName;
  const partner = personTemplate(idFrom(state, "person"), uniqueName, gender, state.world.year - Math.max(18, ageOf(person, state.world.year)), person.householdId);
  seedTalents(state, partner);
  partner.career = { industry: "一般產業", occupation: "專業人員", status: "employed", level: 1, experience: 1, annualIncome: 120 };
  partner.partnerIds = [person.id];
  partner.currentPartnerId = person.id;
  partner.relationshipStage = "married";
  person.partnerIds = unique([...person.partnerIds, partner.id]);
  person.currentPartnerId = partner.id;
  person.relationshipStage = "married";
  state.people[partner.id] = partner;
  state.households[person.householdId].memberIds.push(partner.id);
  state.dynasty.generationByPersonId[partner.id] = state.dynasty.generationByPersonId[person.id];
  return partner;
}

function startDatingCandidate(state: LogicGameState, personId: string, candidate: RelationshipCandidate) {
  const person = state.people[personId];
  if (!person || person.relationshipStage !== "single") return undefined;
  const existing = state.people[candidate.id];
  if (existing && alive(existing)) {
    existing.flags = existing.flags.filter((flag) => flag !== "former-partner-hidden");
    existing.partnerIds = unique([...existing.partnerIds, person.id]);
    existing.currentPartnerId = person.id;
    existing.relationshipStage = "dating";
    person.partnerIds = unique([...person.partnerIds, existing.id]);
    person.currentPartnerId = existing.id;
    person.relationshipStage = "dating";
    person.longTermStates.datingSinceYear = state.world.year;
    existing.longTermStates.datingSinceYear = state.world.year;
    person.longTermStates.reunitedRelationship = true;
    existing.longTermStates.reunitedRelationship = true;
    return existing;
  }
  const householdId = idFrom(state, "household-dating");
  const partner = personTemplate(candidate.id, candidate.name, candidate.gender, candidate.birthYear, householdId);
  seedTalents(state, partner);
  partner.stats = { ...candidate.stats };
  partner.career = { ...candidate.career };
  partner.partnerIds = [person.id];
  partner.currentPartnerId = person.id;
  partner.relationshipStage = "dating";
  person.partnerIds = unique([...person.partnerIds, partner.id]);
  person.currentPartnerId = partner.id;
  person.relationshipStage = "dating";
  person.longTermStates.datingSinceYear = state.world.year;
  partner.longTermStates.datingSinceYear = state.world.year;
  delete person.longTermStates.reunitedRelationship;
  delete partner.longTermStates.reunitedRelationship;
  state.people[partner.id] = partner;
  state.households[householdId] = {
    id: householdId,
    name: `${partner.name}的家庭`,
    assets: 80,
    memberIds: [partner.id],
    dependentIds: [],
    supportShares: {},
    investmentLevel: 0,
    businessLevel: 0,
    familyPlan: "undecided",
    annualLedger: { year: state.world.year, income: 0, expenses: 0, net: 0, details: [] },
    flags: [],
  };
  state.dynasty.generationByPersonId[partner.id] = state.dynasty.generationByPersonId[person.id];
  return partner;
}

function marryCurrentPartner(state: LogicGameState, personId: string) {
  const person = state.people[personId];
  const partner = person?.currentPartnerId ? state.people[person.currentPartnerId] : undefined;
  if (!person || !partner || person.relationshipStage !== "dating") return false;
  const destination = state.households[person.householdId];
  const source = state.households[partner.householdId];
  if (!destination || !source) return false;
  destination.assets += source.assets;
  destination.memberIds = unique([...destination.memberIds, partner.id]);
  partner.householdId = destination.id;
  person.relationshipStage = "married";
  partner.relationshipStage = "married";
  delete person.longTermStates.datingSinceYear;
  delete partner.longTermStates.datingSinceYear;
  delete person.longTermStates.reunitedRelationship;
  delete partner.longTermStates.reunitedRelationship;
  delete state.households[source.id];
  return true;
}

function endRelationship(state: LogicGameState, personId: string) {
  const person = state.people[personId];
  const partner = person?.currentPartnerId ? state.people[person.currentPartnerId] : undefined;
  if (!person || !partner) return false;
  person.relationshipStage = "single";
  partner.relationshipStage = "single";
  person.currentPartnerId = undefined;
  partner.currentPartnerId = undefined;
  delete person.longTermStates.datingSinceYear;
  delete partner.longTermStates.datingSinceYear;
  delete person.longTermStates.reunitedRelationship;
  delete partner.longTermStates.reunitedRelationship;
  partner.flags = unique([...partner.flags, "former-partner-hidden"]);
  return true;
}

export function adoptChild(state: LogicGameState, parentId = state.activePersonId) {
  const parent = state.people[parentId];
  const household = state.households[parent.householdId];
  const genderRoll = nextRng(state.rng);
  state.rng = genderRoll.rng;
  const gender = genderRoll.value < 0.5 ? "female" : "male";
  const child = personTemplate(
    idFrom(state, "person"),
    uniqueFullName(state, gender, { familyName: state.dynasty.familyName, preferDynastyName: true }),
    gender,
    state.world.year,
    household.id,
  );
  seedTalents(state, child);
  child.flags.push("adopted");
  const partnerId = parent.relationshipStage === "married" && parent.currentPartnerId && state.people[parent.currentPartnerId] && alive(state.people[parent.currentPartnerId])
    ? parent.currentPartnerId
    : undefined;
  child.parentIds = partnerId ? [parent.id, partnerId] : [parent.id];
  const statRoll = (min: number, max: number) => {
    const roll = randomInt(state.rng, min, max);
    state.rng = roll.rng;
    return roll.value;
  };
  child.stats = {
    stamina: statRoll(34, 74),
    intellect: statRoll(34, 78),
    charm: statRoll(34, 76),
    empathy: statRoll(38, 82),
    luck: statRoll(25, 85),
    health: statRoll(48, 90),
  };
  parent.childIds.push(child.id);
  if (partnerId) state.people[partnerId].childIds.push(child.id);
  state.people[child.id] = child;
  household.memberIds.push(child.id);
  household.dependentIds.push(child.id);
  state.dynasty.generationByPersonId[child.id] = (state.dynasty.generationByPersonId[parent.id] || state.world.generationArc) + 1;
  state.newbornIdsAwaitingName.push(child.id);
  state.logs.push({ year: state.world.year, personId: child.id, title: `${child.name} 加入家庭`, text: "領養完成後，孩子擁有與親生子女相同的家族關係與成長事件。", tags: ["adoption"] });
  return child;
}

export function nameNewborns(source: LogicGameState, names: Record<string, string>) {
  const state = structuredClone(source);
  const used = new Set(
    Object.values(state.people)
      .filter((person) => !state.newbornIdsAwaitingName.includes(person.id))
      .map((person) => person.name),
  );
  for (const id of state.newbornIdsAwaitingName) {
    const child = state.people[id];
    if (!child) continue;
    const given = String(names[id] || "").trim().replace(/\s/g, "").slice(0, 8);
    if (!given) throw new Error("Newborn name is required");
    const fullName = `${state.dynasty.familyName}${given}`;
    if (used.has(fullName)) throw new Error("Newborn names must be unique");
    used.add(fullName);
    child.name = fullName;
  }
  state.newbornIdsAwaitingName = [];
  return state;
}

export function resolveAdoptionNames(
  source: LogicGameState,
  mode: "keep" | "change",
  names: Record<string, { familyName: string; givenName: string }> = {},
) {
  if (!source.adoptionNameDecision) throw new Error("No adoption name decision");
  const state = structuredClone(source);
  const decision = state.adoptionNameDecision!;
  const used = new Set(
    Object.values(state.people)
      .filter((person) => !decision.childIds.includes(person.id))
      .map((person) => person.name),
  );
  const records: string[] = [];
  for (const id of decision.childIds) {
    const child = state.people[id];
    const original = decision.originalNames[id];
    if (!child || !original) continue;
    const originalFullName = `${original.familyName}${original.givenName}`;
    const input = mode === "change" ? names[id] : original;
    const familyName = String(input?.familyName || "").trim().replace(/\s/g, "").slice(0, 8);
    const givenName = String(input?.givenName || "").trim().replace(/\s/g, "").slice(0, 8);
    if (!familyName || !givenName) throw new Error("每位孩子的姓氏與名字都需要填寫");
    const fullName = `${familyName}${givenName}`;
    if (used.has(fullName)) throw new Error("孩子的姓名不能與家庭中其他人重複");
    used.add(fullName);
    child.name = fullName;
    records.push(fullName === originalFullName
      ? `決定保留 ${originalFullName} 的原姓名`
      : `將姓名由 ${originalFullName} 改為 ${fullName}`);
  }
  state.logs.push({
    year: state.world.year,
    personId: decision.childIds[0],
    title: decision.childIds.length === 1 ? "領養一名孩子加入家庭" : `領養 ${decision.childIds.length} 名孩子加入家庭`,
    text: `${records.join("；")}。孩子正式加入主角家庭，生活與教育費由這個家庭戶負擔。`,
    tags: ["adoption", "family"],
  });
  state.adoptionNameDecision = undefined;
  queueNextChoice(state);
  return state;
}

export function moveAdultToNewHousehold(state: LogicGameState, personId: string) {
  const person = state.people[personId];
  if (!person || ageOf(person, state.world.year) < 18) return undefined;
  const oldHousehold = state.households[person.householdId];
  if (!oldHousehold) return undefined;
  const branchIds = new Set<string>([person.id]);
  person.partnerIds.forEach((id) => {
    const partner = state.people[id];
    if (partner && alive(partner) && partner.householdId === oldHousehold.id) branchIds.add(id);
  });
  person.childIds.forEach((id) => {
    const child = state.people[id];
    if (child && alive(child) && child.householdId === oldHousehold.id && ageOf(child, state.world.year) < 18) branchIds.add(id);
  });
  [...branchIds].forEach((id) => {
    const member = state.people[id];
    member?.childIds.forEach((childId) => {
      const child = state.people[childId];
      if (child && alive(child) && child.householdId === oldHousehold.id && ageOf(child, state.world.year) < 18) branchIds.add(childId);
    });
  });
  const newId = idFrom(state, "household");
  const startingAssets = oldHousehold.assets > 0 ? Math.round(oldHousehold.assets * 0.08) : 0;
  oldHousehold.assets -= startingAssets;
  oldHousehold.memberIds = oldHousehold.memberIds.filter((id) => !branchIds.has(id));
  oldHousehold.dependentIds = oldHousehold.dependentIds.filter((id) => !branchIds.has(id));
  branchIds.forEach((id) => delete oldHousehold.supportShares[id]);
  const pregnancy = oldHousehold.pregnancy && branchIds.has(oldHousehold.pregnancy.gestationalParentId) ? oldHousehold.pregnancy : undefined;
  if (pregnancy) oldHousehold.pregnancy = undefined;
  const household: HouseholdState = {
    id: newId,
    name: `${person.name}的家庭`,
    assets: startingAssets,
    memberIds: [...branchIds],
    dependentIds: [...branchIds].filter((id) => ageOf(state.people[id], state.world.year) < 18),
    supportShares: {},
    investmentLevel: 0,
    businessLevel: 0,
    familyPlan: pregnancy ? oldHousehold.familyPlan : "undecided",
    pregnancy,
    annualLedger: { year: state.world.year, income: 0, expenses: 0, net: 0, details: [] },
    flags: [],
  };
  branchIds.forEach((id) => { state.people[id].householdId = newId; });
  state.households[newId] = household;
  if (branchIds.has(state.activePersonId)) state.activeHouseholdId = newId;
  state.logs.push({ year: state.world.year, personId: person.id, title: `${person.name}的家庭完成分戶`, text: `${household.memberIds.length} 名成員一起搬入新家庭戶，起始資產 ${startingAssets}。`, tags: ["household", "separation"] });
  return household;
}

export function inheritAs(source: LogicGameState, heirId: string, assetRatio?: number, content: ContentPack = TEN_GENERATION_CONTENT) {
  const state = structuredClone(source);
  const heir = state.people[heirId];
  if (!heir || !alive(heir)) throw new Error("Invalid heir");
  const oldHousehold = state.households[state.activeHouseholdId];
  const heirHousehold = state.households[heir.householdId];
  if (!heirHousehold) throw new Error("Heir household missing");
  const nextGeneration = (state.dynasty.generationByPersonId[heir.id] || state.world.generationArc + 1);
  const arc = content.generationArcs.find((item) => item.id === nextGeneration) || content.generationArcs.at(-1)!;
  const ratio = assetRatio ?? Number(arc.modifiers.inheritance || 0.65);
  if (heirHousehold.id !== oldHousehold.id) {
    const inheritedAssets = Math.max(0, Math.round(oldHousehold.assets * ratio));
    const inheritedInvestment = Math.floor(oldHousehold.investmentLevel * ratio);
    const inheritedBusiness = Math.floor(oldHousehold.businessLevel * ratio);
    oldHousehold.assets -= inheritedAssets;
    heirHousehold.assets += inheritedAssets;
    oldHousehold.investmentLevel -= inheritedInvestment;
    oldHousehold.businessLevel -= inheritedBusiness;
    heirHousehold.investmentLevel += inheritedInvestment;
    heirHousehold.businessLevel += inheritedBusiness;
  }
  state.activePersonId = heir.id;
  state.activeHouseholdId = heirHousehold.id;
  state.completedGenerations += 1;
  state.world.generationArc = Math.min(10, nextGeneration);
  state.world.modifiers = { ...arc.modifiers };
  state.pendingChoice = null;
  state.eventLedger.topicLastYear = {};
  const inheritanceText = heirHousehold.id === oldHousehold.id
    ? "繼承人留在原家庭戶，共同資產、同住成員、投資與家業完整延續。"
    : "繼承人保留自己的能力、教育、職涯與關係；現金、投資與家業按比例從原戶移轉一次。";
  state.logs.push({ year: state.world.year, personId: heir.id, title: `第 ${state.world.generationArc} 代由 ${heir.name} 繼承`, text: inheritanceText, tags: ["inheritance"] });
  return state;
}

export function validateLogicState(state: LogicGameState) {
  const errors: string[] = [];
  if (state.schemaVersion !== LOGIC_SCHEMA_VERSION) errors.push("schema-version");
  if (!state.people[state.activePersonId]) errors.push("active-person-missing");
  if (!state.households[state.activeHouseholdId]) errors.push("active-household-missing");
  if (state.people[state.activePersonId]?.householdId !== state.activeHouseholdId) errors.push("active-household-mismatch");
  if (state.pendingChoice && state.pendingChoice.validChoiceIds.length === 0) errors.push("empty-pending-choice");
  if (state.pendingChoice?.targetPersonId) {
    const target = state.people[state.pendingChoice.targetPersonId];
    if (!target) errors.push("pending-target-missing");
    else if (target.householdId !== state.pendingChoice.householdId) errors.push("pending-target-household-mismatch");
  }
  if (state.adoptionNameDecision) {
    for (const id of state.adoptionNameDecision.childIds) {
      if (!state.people[id]) errors.push(`adoption-name-child-missing:${id}`);
      if (!state.adoptionNameDecision.originalNames[id]) errors.push(`adoption-original-name-missing:${id}`);
    }
  }
  for (const person of Object.values(state.people)) {
    if (ageOf(person, state.world.year) < 0) errors.push(`negative-age:${person.id}`);
    if (!state.households[person.householdId]) errors.push(`household-missing:${person.id}`);
    else if (alive(person) && !state.households[person.householdId].memberIds.includes(person.id)) errors.push(`person-not-in-household:${person.id}`);
    for (const id of [...person.parentIds, ...person.partnerIds, ...person.childIds]) {
      if (!state.people[id]) errors.push(`relation-missing:${person.id}:${id}`);
    }
    for (const value of Object.values(person.stats)) if (!Number.isFinite(value)) errors.push(`invalid-stat:${person.id}`);
  }
  for (const household of Object.values(state.households)) {
    if (!Number.isFinite(household.assets)) errors.push(`invalid-assets:${household.id}`);
    if (!Number.isFinite(household.investmentLevel) || household.investmentLevel < 0) errors.push(`invalid-investment:${household.id}`);
    if (!Number.isFinite(household.businessLevel) || household.businessLevel < 0) errors.push(`invalid-business:${household.id}`);
    if (new Set(household.memberIds).size !== household.memberIds.length) errors.push(`duplicate-member:${household.id}`);
    if (household.pregnancy && !state.people[household.pregnancy.gestationalParentId]) errors.push(`pregnant-parent-missing:${household.id}`);
    if (household.adoptionPlacement?.childIds?.some((id) => !state.people[id])) errors.push(`adoption-child-missing:${household.id}`);
    for (const cost of household.recurringCareCosts || []) {
      if (!Number.isFinite(cost.annualAmount) || cost.annualAmount < 0 || cost.endYear < cost.startYear) errors.push(`invalid-care-cost:${household.id}:${cost.id}`);
    }
    household.memberIds.forEach((id) => {
      if (!state.people[id]) errors.push(`member-missing:${household.id}:${id}`);
      if (state.people[id]?.deathYear) errors.push(`dead-household-member:${household.id}:${id}`);
      if (state.people[id]?.householdId !== household.id) errors.push(`member-household-mismatch:${household.id}:${id}`);
    });
  }
  return unique(errors);
}
